/**
 * hourAxis — the 24 hourly buckets of the poll-weight chart, on the reader's
 * clock.
 *
 * The buckets are recorded by the scheduler in *its* wall clock (currently UTC,
 * see `scheduler.DefaultPlanner`), because a feed is shared: two customers in
 * different timezones watch the same calendar, and the engine has to shape one
 * schedule for both. So the stored histogram is a property of the deployment,
 * not of the reader — but a chart labelled "09:00" is read as the reader's own
 * morning, and a customer in Warsaw would be looking at their peak two hours
 * early without ever being told.
 *
 * The conversion is a rotation: each bucket covers one hour starting at
 * `bucket * 60` minutes in the bucket zone, which is
 * `bucket * 60 + shift` minutes on the reader's clock, where `shift` is the
 * difference between the two offsets *now*. Two consequences the copy states
 * rather than hides:
 *
 *   - A bucket is an hour wide, so in a zone offset by a fraction of an hour
 *     (India, +05:30) the shifted bucket starts at :30. The label says so
 *     instead of rounding the data onto an hour boundary it never had.
 *   - A 28-day window can contain a daylight-saving change, in which case part
 *     of the aggregate sat at a different offset. There is no way to un-mix it
 *     after the counts were folded into 24 buckets, so the chart says that the
 *     shift is the current one.
 *
 * The offsets are passed in rather than read from the environment so the
 * behaviour is testable: `viewerOffsetMinutes` defaults to the browser's own,
 * and `bucketOffsetMinutes` comes from the API (`histogram_utc_offset_minutes`).
 */

/** Minutes east of UTC for the browser, honouring whatever DST is in effect. */
export function viewerOffsetMinutes(now = new Date()) {
  // getTimezoneOffset is minutes *west* of UTC, and positive for the Americas.
  return -now.getTimezoneOffset();
}

/**
 * hourAxis places the buckets on the reader's clock.
 *
 * @param weights number[24], the normalized bar heights, indexed by bucket in
 *   the bucket zone (API order).
 * @param bucketOffsetMinutes minutes east of UTC for the bucket zone.
 * @param viewerOffsetMinutes minutes east of UTC for the reader; defaults to
 *   the browser's.
 * @returns {{bars: {bucket: number, label: string, weight: number, startMinutes: number}[],
 *            shiftMinutes: number, viewerOffsetMinutes: number, bucketOffsetMinutes: number}}
 *   `bars` is ordered by local time, so the chart reads as the reader's own day.
 */
export function hourAxis({
  weights = [],
  bucketOffsetMinutes = 0,
  viewerOffsetMinutes: viewer = null,
  now = new Date(),
} = {}) {
  // The parameter is aliased to `viewer` in the destructuring, so the exported
  // helper keeps its name here.
  const viewerMinutes = viewer == null ? viewerOffsetMinutes(now) : viewer;
  const shift = viewerMinutes - bucketOffsetMinutes;

  const bars = weights.map((weight, bucket) => {
    const startMinutes = mod(bucket * 60 + shift, 24 * 60);
    return {
      bucket,
      weight,
      startMinutes,
      label: hourLabel(startMinutes),
    };
  });
  bars.sort((a, b) => a.startMinutes - b.startMinutes || a.bucket - b.bucket);

  return {
    bars,
    shiftMinutes: shift,
    viewerOffsetMinutes: viewerMinutes,
    bucketOffsetMinutes,
  };
}

/** hourLabel renders minutes-from-midnight as "HH:MM". */
export function hourLabel(minutes) {
  const total = mod(Math.round(minutes), 24 * 60);
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

/**
 * offsetLabel renders minutes east of UTC the way a person writes a zone:
 * "UTC", "UTC+2", "UTC+5:30", "UTC−3" (with a real minus sign, because
 * "UTC-3" in a sentence reads as a range).
 */
export function offsetLabel(minutes) {
  const total = Math.round(minutes);
  if (total === 0) return "UTC";
  const sign = total > 0 ? "+" : "−";
  const absolute = Math.abs(total);
  const hours = Math.floor(absolute / 60);
  const mins = absolute % 60;
  return mins === 0 ? `UTC${sign}${hours}` : `UTC${sign}${hours}:${String(mins).padStart(2, "0")}`;
}

function mod(value, modulus) {
  return ((value % modulus) + modulus) % modulus;
}
